import { RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMarcarRevisado } from "@/hooks/useRegistroDeTerreno";
import { useResaltadoDeSincronizaciones } from "@/hooks/useResaltadoDeSincronizaciones";
import type { RegistroDeTerrenoDelInforme } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import { EtiquetaNueva } from "./FiltroDeSincronizaciones";
import {
  cuantosPorSincronizacion,
  cuantosResaltados,
  elementosDeLaPestana,
  esPestanaConBarra,
  sincronizacionesQueAportan,
  textoDeLaBarra,
  textoDeUnidades,
} from "./resaltado";
import { cuantasSincronizacionesNuevas, etiquetaDeSincronizacion } from "./sincronizaciones";

interface BarraDeSincronizacionesProps {
  informeId: number;
  /** La pestaña activa del editor; en las que no llevan barra no dibuja nada. */
  pestana: string;
  registro: RegistroDeTerrenoDelInforme | undefined;
}

/**
 * La barra bajo el título de cada pestaña técnica: dice qué se está resaltando
 * (lo que llegó en las sincronizaciones elegidas) y deja elegir otras.
 *
 * Resalta, nunca filtra: lo que cambia es el color de lo recibido, no lo que se
 * ve. El filtro que esconde vive solo en "Registro de terreno".
 */
export function BarraDeSincronizaciones({ informeId, pestana, registro }: BarraDeSincronizacionesProps) {
  const resaltado = useResaltadoDeSincronizaciones();
  const marcarRevisado = useMarcarRevisado(informeId);
  const [abierto, setAbierto] = useState(false);

  const { sincronizaciones, resaltadas } = resaltado;

  const { unidad, elementos } = useMemo(
    () =>
      esPestanaConBarra(pestana)
        ? elementosDeLaPestana(registro, pestana)
        : { unidad: "capturas" as const, elementos: [] },
    [registro, pestana],
  );

  if (!esPestanaConBarra(pestana) || sincronizaciones.length === 0) return null;

  const aportan = sincronizacionesQueAportan(elementos, sincronizaciones);
  const total = cuantosResaltados(elementos, resaltadas);
  const hay = total > 0;
  const nuevas = cuantasSincronizacionesNuevas(sincronizaciones);
  const etiquetas = aportan.filter((s) => resaltadas.has(s.id)).map((s) => etiquetaDeSincronizacion(s.en));

  function irAlPrimero(evento: React.MouseEvent) {
    evento.preventDefault();

    const primero = document.querySelector<HTMLElement>('[data-syhl="1"]');
    if (!primero) return;

    // El contenedor que desplaza es el `main` del layout: `scroll-margin` deja el
    // aire sin preguntar cuál es, y sirve igual dentro de un panel con scroll.
    primero.style.scrollMarginTop = "120px";
    primero.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function alternar(id: string, marcada: boolean) {
    const base = new Set(resaltadas);
    if (marcada) base.add(id);
    else base.delete(id);

    resaltado.elegir(sincronizaciones.filter((s) => base.has(s.id)).map((s) => s.id));
  }

  return (
    <section
      aria-label="Sincronizaciones resaltadas"
      className={cn(
        "mt-3 flex flex-wrap items-center gap-2.5 rounded-[14px] border-[1.5px] py-[9px] pr-3 pl-4",
        hay ? "bg-sync-highlight border-sync-highlight-border" : "bg-superficie-sutil border-divisor",
      )}
    >
      <span
        aria-hidden="true"
        className={cn("size-2 flex-none rounded-full", hay ? "bg-sync-accent" : "bg-ink-ghost")}
      />
      <p className="text-ink min-w-0 flex-1 text-[0.78125rem] font-bold text-pretty">
        {textoDeLaBarra({ resaltados: total, unidad, etiquetas })}
      </p>

      {hay && (
        <a
          href="#primero-resaltado"
          onClick={irAlPrimero}
          className="text-sync-tag-foreground focus-visible:ring-ring/50 rounded-sm text-xs font-bold outline-none focus-visible:ring-[3px]"
        >
          Ir al primero ↓
        </a>
      )}

      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="gap-2 border-[1.5px]">
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Sincronizaciones
            {nuevas > 0 && (
              <span className="bg-sync-accent rounded-[5px] px-1.5 py-px text-[0.59375rem] font-extrabold text-white">
                {nuevas} {nuevas === 1 ? "nueva" : "nuevas"}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[21.25rem] p-0">
          <div className="border-divisor flex items-center justify-between border-b px-3.5 py-2.5">
            <span className="text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.07em] uppercase">
              Resaltar lo recibido en
            </span>
            <PopoverClose className="text-ink-body hover:text-ink focus-visible:ring-ring/50 rounded-sm text-xs font-bold outline-none focus-visible:ring-[3px]">
              Cerrar
            </PopoverClose>
          </div>

          <div className="flex max-h-72 flex-col overflow-y-auto py-1">
            {aportan.length === 0 && (
              <p className="text-ink-muted px-3.5 py-3 text-xs font-semibold text-pretty">
                Ninguna sincronización trajo datos para esta pestaña.
              </p>
            )}
            {aportan.map((s) => (
              <label
                key={s.id}
                className="hover:bg-muted/50 flex min-h-11 cursor-pointer items-center gap-3 px-3.5 py-1.5"
              >
                <Checkbox
                  checked={resaltadas.has(s.id)}
                  onCheckedChange={(v) => alternar(s.id, v === true)}
                  className="data-[state=checked]:bg-sync-accent data-[state=checked]:border-sync-accent dark:data-[state=checked]:bg-sync-accent size-4 rounded-[5px] data-[state=checked]:text-white"
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-ink font-mono text-xs font-bold">
                    {etiquetaDeSincronizacion(s.en)}
                  </span>
                  <span className="text-ink-muted text-[0.6875rem] font-semibold">
                    {textoDeUnidades(cuantosPorSincronizacion(elementos, s.id), unidad)}
                  </span>
                </span>
                {s.nueva && <EtiquetaNueva />}
              </label>
            ))}
          </div>

          <div className="border-divisor flex items-center justify-between gap-2 border-t px-3.5 py-2.5">
            <button
              type="button"
              onClick={resaltado.reiniciar}
              className="text-ink-body hover:text-ink focus-visible:ring-ring/50 rounded-sm text-xs font-bold outline-none focus-visible:ring-[3px]"
            >
              Quitar resaltado
            </button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={nuevas === 0 || marcarRevisado.isPending}
              onClick={() => {
                const ultima = sincronizaciones[sincronizaciones.length - 1];
                if (ultima) marcarRevisado.mutate(ultima.id);
              }}
            >
              Marcar como revisado
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </section>
  );
}
